import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { toast } from "sonner";
import { Database, ArrowClockwise } from "@phosphor-icons/react";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sep 25 2026, user's explicit ask ("add a pick that tells for overall
// refresh data") - one at-a-glance table of every SAP master-data
// cache's last refresh time/result, backed by /admin/data-sync-status.
// "Refresh Now" is only offered for caches with an existing manual
// trigger endpoint - the rest already run on a tight (5-30 min) or
// nightly schedule, no urgent manual action needed for those.
const REFRESH_ACTIONS = {
  suppliers: { trigger: "post:/suppliers/sync-from-sap", poll: (jobId) => `/suppliers/sync-from-sap/status/${jobId}` },
  bom_cache: { trigger: "post:/bom-cache/refresh" },
  supplier_po_cache: { trigger: "post:/admin/purchase-orders/refresh-cache", poll: (jobId) => `/admin/purchase-orders/refresh-cache/poll/${jobId}` },
  full_sync: { trigger: "post:/admin/run-full-sync" },
};

function minutesAgo(isoString) {
  if (!isoString) return null;
  return (Date.now() - new Date(isoString).getTime()) / 60000;
}

function formatAgo(isoString) {
  const mins = minutesAgo(isoString);
  if (mins === null) return "Never";
  if (mins < 1) return "Just now";
  if (mins < 60) return `${Math.round(mins)} min ago`;
  if (mins < 24 * 60) return `${Math.round(mins / 60)} hr ago`;
  return `${Math.round(mins / 1440)} days ago`;
}

export default function DataSyncStatusPage() {
  const [caches, setCaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshingKey, setRefreshingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/data-sync-status`);
      setCaches(data.caches || []);
    } catch (err) {
      toast.error("Could not load data sync status", { description: err?.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refreshNow = async (key) => {
    const action = REFRESH_ACTIONS[key];
    if (!action) return;
    setRefreshingKey(key);
    try {
      const [method, path] = action.trigger.split(":");
      const { data } = await axios[method](`${API}${path}`);
      if (data.already_running) {
        toast.info("A run is already in progress for this cache");
      } else if (action.poll && data.job_id) {
        for (let i = 0; i < 40; i++) {
          await new Promise((r) => setTimeout(r, 3000));
          const { data: status } = await axios.get(`${API}${action.poll(data.job_id)}`);
          if (status.status !== "running") {
            if (status.status === "failed" || status.status === "error") {
              toast.error("Refresh failed", { description: status.error });
            } else {
              toast.success("Refresh complete");
            }
            break;
          }
        }
      } else {
        toast.success("Refresh triggered - this can take a few minutes, check back shortly");
      }
    } catch (err) {
      toast.error("Could not trigger refresh", { description: err?.response?.data?.detail || err.message });
    } finally {
      setRefreshingKey(null);
      load();
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <div className="bg-[#0E7C86] px-4 sm:px-6 py-3 flex items-center gap-3">
        <Database size={22} weight="bold" className="text-white shrink-0" />
        <span className="text-white font-heading font-bold text-base shrink-0">Materials Hub</span>
        <NavTabs />
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </div>

      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1D2939] font-heading" data-testid="data-sync-status-title">
              Data Sync Status
            </h1>
            <p className="text-sm text-[#667085] font-sans mt-0.5">
              When each SAP master-data cache was last refreshed, and whether that run succeeded.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="data-sync-status-refresh-button" className="gap-1.5 shrink-0">
            <ArrowClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
            Refresh
          </Button>
        </div>

        <div className="bg-white border border-[#D0D5DD] rounded-md overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#EAECF0] text-left text-xs text-[#667085] font-sans">
                <th className="px-3 py-2 font-medium">Cache</th>
                <th className="px-3 py-2 font-medium">Last Refreshed</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Detail</th>
                <th className="px-3 py-2 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-[#667085] text-sm" data-testid="data-sync-status-loading">
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && caches.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-[#667085] text-sm" data-testid="data-sync-status-empty">
                    No sync data yet.
                  </td>
                </tr>
              )}
              {caches.map((c) => (
                <tr key={c.key} className="border-b border-[#F2F4F7] hover:bg-slate-50" data-testid={`data-sync-status-row-${c.key}`}>
                  <td className="px-3 py-2 font-semibold text-[#1D2939]">{c.label}</td>
                  <td className="px-3 py-2 text-[#475467]" data-testid={`data-sync-status-lastrun-${c.key}`}>{formatAgo(c.last_run_at)}</td>
                  <td className="px-3 py-2">
                    {c.status ? (
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                          c.status === "ok" || c.status === "done" ? "bg-[#ECFDF3] text-[#027A48]" : "bg-[#FEF3F2] text-[#B42318]"
                        }`}
                        data-testid={`data-sync-status-pill-${c.key}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${c.status === "ok" || c.status === "done" ? "bg-[#12B76A]" : "bg-[#F04438]"}`} />
                        {c.status === "ok" || c.status === "done" ? "OK" : "Error"}
                      </span>
                    ) : (
                      <span className="text-xs text-[#98A2B3]">Never run</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[#475467] font-mono text-xs truncate max-w-[280px]">
                    {c.error || (c.detail ? JSON.stringify(c.detail) : "-")}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {REFRESH_ACTIONS[c.key] && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={refreshingKey === c.key}
                        onClick={() => refreshNow(c.key)}
                        data-testid={`data-sync-status-refresh-now-${c.key}`}
                      >
                        {refreshingKey === c.key ? "Refreshing..." : "Refresh Now"}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
